import {MxSelector, MxSelectorCreator, NO_NAME_PREFIX} from '@aviary/store/store.types';
import {createFeatureSelector, createSelector} from '@ngrx/store';
import {CatalogState} from '@store/catalog/catalog.state';
import {
  CarouselDisplay,
  CATALOG_FEATURE_KEY,
  CatalogRecord,
  CatalogRecordAvailability,
  CatalogRecordAvailabilityBranch,
  CatalogRecordAvailabilityDetail,
  CatalogSearchFilterEntry
} from '@store/catalog/catalog.types';
import {
  generateIndependentSelectorCreatorsFor,
  generateLoadStatusSelectorsFor,
  generateSelectorCreatorsDependentOn,
  generateSelectorsDependentOn
} from '@aviary/store/store.selector-generators';
import {
  FALLBACK_CATALOG_RECORD,
  FALLBACK_CATALOG_RECORD_AVAILABILITY,
  FALLBACK_CATALOG_RECORD_AVAILABILITY_BRANCH,
  FALLBACK_CATALOG_RECORD_AVAILABILITY_DETAIL,
  FALLBACK_CIRCULATION_ITEM,
  FALLBACK_MARC_DETAILS
} from '@store/catalog/catalog.fallback';
import {FALLBACK_MATERIAL_TYPE} from '@aviary/store/common/common.fallback';
import {isPopulatedArray, isPositiveNumber} from '@aviary/store/common/typing.helpers';
import {ListFilterData, ListFilterSelectOptions} from '@aviary/store/common/common.types';
import {generateListFilterSelectOptionsSelectorFrom} from '@aviary/store/common/list-data.helpers';

const entireState: MxSelector<CatalogState> = createFeatureSelector(CATALOG_FEATURE_KEY);

export namespace CatalogSelectors {

  export namespace Current {
    const subState: MxSelector<CatalogState['current']> = createSelector(
      entireState,
      state => state.current
    );

    export const {
      loading,
      awaitingFirstLoad,
      awaitingAnyLoad,
      loaded
    } = generateLoadStatusSelectorsFor(subState);

    export const catalogRecord: MxSelector<CatalogRecord> = createSelector(
      subState,
      ({data}) => data ?? FALLBACK_CATALOG_RECORD
    );

    export const {
      id,
      title,
      author,
      description,
      availability,
      marcDetails,
      materialType,
      identifierType,
      identifier,
      fromIll,
      imageUrl
    } = generateSelectorsDependentOn(catalogRecord, FALLBACK_CATALOG_RECORD, NO_NAME_PREFIX);

    export const {
      materialTypeDescription
    } = generateSelectorsDependentOn(materialType, FALLBACK_MATERIAL_TYPE, 'materialType');

    export const {
      marcDetailsLanguageCode,
      marcDetailsPublisher,
      marcDetailsPublicationDate,
      marcDetailsPhysicalDescription,
      marcDetailsSeriesString,
      marcDetailsSubjects,
      marcDetailsGenres,
      marcDetailsControlNumbers,
      marcDetailsIdentifiers,
    } = generateSelectorsDependentOn(marcDetails, FALLBACK_MARC_DETAILS, 'marcDetails');

    export const displayableControlNumbers: MxSelector<string[]> = createSelector(
      marcDetailsControlNumbers,
      numbers => Object.values(numbers)
    );

    export const displayableIdentifiers: MxSelector<string[]> = createSelector(
      marcDetailsIdentifiers,
      identifiers => isPopulatedArray(identifiers['ISBN']) ? identifiers['ISBN'] : identifiers['isbn'] ?? []
    );

    export const publicationDate: MxSelector<string> = createSelector(
      marcDetails,
      details => details.clean.publicationDate ? details.clean.publicationDate : details.publicationDate
    );
  } // namespace Current

  export namespace Availability {
    const subState: MxSelector<CatalogState['availability']> = createSelector(
      entireState,
      state => state.availability
    );

    export const {
      loading,
      awaitingFirstLoad,
      awaitingAnyLoad,
      loaded
    } = generateLoadStatusSelectorsFor(subState);

    export const availability: MxSelector<CatalogRecordAvailabilityDetail> = createSelector(
      subState,
      ({data}) => data ?? FALLBACK_CATALOG_RECORD_AVAILABILITY_DETAIL
    );

    export const branchAvailability: MxSelector<CatalogRecordAvailabilityBranch[]> = createSelector(
      availability,
      (_availability) => _availability.branchAvailability
    );

    export const recordAvailability: MxSelector<CatalogRecordAvailability> = createSelector(
      availability,
      (_availability) => _availability.totals ?? FALLBACK_CATALOG_RECORD_AVAILABILITY
    );

    export const {
      branchCirculationItemsFor,
      branchBranchNameFor,
      branchAvailableCountFor,
      branchOpacVisibleCopiesFor,
    } = generateIndependentSelectorCreatorsFor(FALLBACK_CATALOG_RECORD_AVAILABILITY_BRANCH, 'branch');

    export const branchHasAvailableFor: MxSelectorCreator<[CatalogRecordAvailabilityBranch], boolean> =
      _branchAvailability => createSelector(
        branchAvailableCountFor(_branchAvailability),
        (count) => count > 0
      );

    export const onlyOneBranchAvailableFor: MxSelectorCreator<[CatalogRecordAvailabilityBranch], boolean> =
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Need to satisfy the selectorCreator type constraint but don't need the branch.
      _availabilityBranch => createSelector(
        branchAvailability,
        (_branchAvailability) => _branchAvailability.length === 1
      );

    export const {
      itemCollectionNameFor,
      itemCallNumberFor,
      itemShelfNameFor,
      itemStatusFor,
    } = generateIndependentSelectorCreatorsFor(FALLBACK_CIRCULATION_ITEM, 'item');

    export const {
      recordAvailableCountFor,
      recordAvailableAtPatronBranchFor,
      recordHoldsInQueueFor,
      recordCirculatingCopiesFor,
    } = generateIndependentSelectorCreatorsFor(FALLBACK_CATALOG_RECORD_AVAILABILITY, 'record');

    export const availableAtPatronsBranchFor: MxSelectorCreator<[CatalogRecordAvailability], boolean> =
      _availability => createSelector(
        recordAvailableAtPatronBranchFor(_availability),
        isPositiveNumber
      );

    export const availableAtOtherBranchesFor: MxSelectorCreator<[CatalogRecordAvailability], number> =
      _availability => createSelector(
        recordAvailableCountFor(_availability),
        recordAvailableAtPatronBranchFor(_availability),
        (available, availableAtPatronBranch) => Math.max(0, available - availableAtPatronBranch)
      );
  } // namespace Availability

  export namespace RelatedItems {
    const subState: MxSelector<CatalogState['related']> = createSelector(
      entireState,
      state => state.related
    );

    export const {
      loading,
      awaitingFirstLoad,
      awaitingAnyLoad,
      loaded
    } = generateLoadStatusSelectorsFor(subState);

    export const relatedItems: MxSelector<CatalogRecord[]> = createSelector(
      subState,
      ({data}) => data ?? []
    );
  } // namespace RelatedItems

  export namespace CatalogCarousels {
    const subState: MxSelector<CatalogState['carousels']> = createSelector(
      entireState,
      state => state.carousels
    );

    // LOAD STATUS
    export const {
      loading,
      awaitingFirstLoad,
      awaitingAnyLoad,
      loaded
    } = generateLoadStatusSelectorsFor(subState);

    // CAROUSELS
    export const catalogCarousels: MxSelector<CarouselDisplay[]> = createSelector(
      subState,
      ({data}) => data ?? []
    );
  } // namespace CatalogCarousels

  export namespace List {
    const subState: MxSelector<CatalogState['list']> = createSelector(
      entireState,
      state => state.list
    );

    export const {
      loading,
      awaitingFirstLoad,
      awaitingAnyLoad,
      loaded
    } = generateLoadStatusSelectorsFor(subState);

    export const records: MxSelector<CatalogRecord[]> = createSelector(
      subState,
      ({data}) => data ?? []
    );

    export const filterData: MxSelector<ListFilterData> = createSelector(
      subState,
      ({filterData}) => filterData ?? {}
    );

    export const recordIds: MxSelector<number[]> = createSelector(
      records,
      _records => _records.map(record => record.id)
    );

    export const filterSelectOptionsFor: MxSelectorCreator<[string], ListFilterSelectOptions> =
      filterName => generateListFilterSelectOptionsSelectorFrom(subState, filterName);

    export const filterSelectOptionsCountFor: MxSelectorCreator<[string], number> =
      filterName => createSelector(
        filterSelectOptionsFor(filterName),
        options => options.length
      );

    export const hasFilterSelectOptionsFor: MxSelectorCreator<[string], boolean> =
      filterName => createSelector(
        filterSelectOptionsCountFor(filterName),
        isPositiveNumber
      );

    export const firstPopulatedFilterFor: MxSelectorCreator<[CatalogSearchFilterEntry[]], CatalogSearchFilterEntry | undefined> =
      filters => createSelector(
        filterData,
        (filterData) =>
          filters.find((entry) => isPopulatedArray(filterData[entry.filterName]))
      );
  } // namespace List

  export namespace Stateless {
    const independentSelectorCreators = generateIndependentSelectorCreatorsFor(FALLBACK_CATALOG_RECORD, NO_NAME_PREFIX);

    export const {
      idFor,
      titleFor,
      authorFor,
      identifierFor,
      materialTypeFor,
      imageUrlFor,
      marcRecordFor,
      marcDetailsFor,
      availabilityFor,
    } = independentSelectorCreators;

    export const {
      materialTypeNameFor,
      materialTypeDescriptionFor
    } = generateSelectorCreatorsDependentOn(materialTypeFor, FALLBACK_MATERIAL_TYPE, 'materialType');

    export const {
      marcDetailsSeriesStringFor,
    } = generateSelectorCreatorsDependentOn(marcDetailsFor, FALLBACK_MARC_DETAILS, 'marcDetails');

    export const publicationDate: MxSelectorCreator<[CatalogRecord], string> =
      _catalogRecord => createSelector(
        marcDetailsFor(_catalogRecord),
        details => details.clean?.publicationDate ? details.clean.publicationDate : details.publicationDate
      );

    export const idAsStringFor: MxSelectorCreator<[CatalogRecord], string> =
      _catalogRecord => createSelector(
        idFor(_catalogRecord),
        String
      );

    export const quotedTitleFor: MxSelectorCreator<[CatalogRecord], string> =
      _catalogRecord => createSelector(
        titleFor(_catalogRecord),
        title => `"${title}"`
      );

  } // namespace Stateless

  export namespace Favorites {
    const subState: MxSelector<CatalogState['favorites']> = createSelector(
      entireState,
      state => state.favorites
    );

    // LOAD STATUS
    export const {
      loading,
      awaitingFirstLoad,
      awaitingAnyLoad,
      loaded,
      finishedLoading
    } = generateLoadStatusSelectorsFor(subState);

    // FAVORITES
    export const favorites: MxSelector<CatalogRecord[]> = createSelector(
      subState,
      ({data}) => data ?? []
    );

    export const hasFavorites: MxSelector<boolean> = createSelector(
      favorites,
      isPopulatedArray
    );

    export const ids: MxSelector<number[]> = createSelector(
      favorites,
      _favorites => _favorites.map(favorite => favorite.id)
    );
  } // namespace Favorites

  export namespace FavoritesIds {
    const subState: MxSelector<CatalogState['favoritesIds']> = createSelector(
      entireState,
      state => state.favoritesIds
    );

    export const {
      loading,
      awaitingFirstLoad,
      awaitingAnyLoad,
      loaded,
      finishedLoading
    } = generateLoadStatusSelectorsFor(subState);

    export const favoritesIds: MxSelector<number[]> = createSelector(
      subState,
      ({data}) => data ?? []
    );

    export const isFavorite: MxSelectorCreator<[number], boolean> =
      _catalogRecordId => createSelector(
        favoritesIds,
        _favoritesIds => _favoritesIds.includes(_catalogRecordId)
      );

    export const isNotFavorite: MxSelectorCreator<[number], boolean> =
      _catalogRecordId => createSelector(
        isFavorite(_catalogRecordId),
        _isFavorite => !_isFavorite
      );
  } // namespace FavoritesIds
} // namespace CatalogSelectors
